import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import {
  generateOtpCode,
  verifyOtp,
  verifyClearError,
  smsOtpVerified
} from "../../store/actions/otp";
import { getOtp } from "../../store/selectors/otp";
import {
  getApplicantInfo,
  getIsEmailChanged,
  getIsMultiPartnerJourney,
  getIsSignatoryPrimaryEmailChanged,
  getProspectId
} from "../../store/selectors/appConfig";
import { getsIsContactAndSignatoryDetailsChanged } from "../../store/selectors/sendProspectToAPI";
import { useTrackingHistory } from "../../utils/useTrackingHistory";

import { MAX_ATTEMPT_ALLOWED } from "./constants";
import { Form } from "./components/Form";
import { OtpChannel } from "../../constants";
import { triggerDecisions } from "../../store/actions/decisions";
import { getDocumentsList } from "../../store/actions/uploadDocuments";
import routes from "../../routes";
import { updateProspectId } from "../../store/actions/appConfig";
import { resetLogin } from "../../store/actions/loginForm";

export const Otp = ({
  redirectRoute,
  otpType,
  title,
  info,
  changeText,
  roInviteProp,
  heading,
  selectedPartner,
  partnerInviteProp,
  isFromPreview
}) => {
  const dispatch = useDispatch();
  const {
    attempts,
    verificationError,
    isVerified,
    isPending,
    isGenerating,
    maxAttemptsExceeded,
    maxAttemptsServerError
  } = useSelector(getOtp);
  const prospectId = useSelector(getProspectId);
  const isMultiPartnerJourney = useSelector(getIsMultiPartnerJourney);
  const applicantInfo = useSelector(getApplicantInfo);
  const isContactAndSignatoryDetailsChanged = useSelector(getsIsContactAndSignatoryDetailsChanged);
  const isEmailChanged = useSelector(getIsEmailChanged);
  const isSignatoryEmailIdChanged = useSelector(getIsSignatoryPrimaryEmailChanged);

  const searchParam = useLocation().search;
  const pushHistory = useTrackingHistory();

  const [code, setCode] = useState(Array(6).fill(""));
  const [loginAttempt, setLoginAttempt] = useState(0);

  const otpRef = useRef(null);

  const resetOtpForm = useCallback(() => {
    setCode(Array(6).fill(""));
    otpRef.current.resetFocus();
  }, [setCode]);

  //if user is verified
  useEffect(() => {
    if (isVerified) {
      if (
        otpType === OtpChannel.Sms &&
        redirectRoute !== routes.MyApplications &&
        redirectRoute !== routes.efrOTPVerification &&
        redirectRoute !== routes.stakeholdersInfo &&
        !partnerInviteProp &&
        !isContactAndSignatoryDetailsChanged
      ) {
        if (isMultiPartnerJourney && selectedPartner && Object.keys(selectedPartner).length > 0) {
          if (isEmailChanged || isSignatoryEmailIdChanged) {
            dispatch(smsOtpVerified());
            const {
              signatoryId,
              contactDetails: { primaryEmail, primaryMobCountryCode, primaryMobileNo }
            } = selectedPartner;

            const otpData = {
              email: primaryEmail,
              mobileNo: primaryMobileNo,
              countryCode: primaryMobCountryCode,
              sid: signatoryId,
              prospectId,
              mode: OtpChannel.Email,
              action: "generate"
            };

            dispatch(generateOtpCode(otpData));
          }
        } else {
          dispatch(smsOtpVerified());
          const otpData = {
            ...applicantInfo,
            prospectId,
            mode: OtpChannel.Email,
            action: "generate"
          };

          dispatch(generateOtpCode(otpData));
        }
      }
      if (otpType === OtpChannel.Email) {
        if (prospectId) {
          dispatch(
            triggerDecisions({
              onValuesChanged: changedValues => {
                // console.log(changedValues);
              },
              inputFields: {
                decision_input: [
                  {
                    input_key: "prospect.applicantInfo.persona",
                    input_value: applicantInfo.persona
                  }
                ]
              }
            })
          );

          //document upload init
          dispatch(getDocumentsList());
        }
      }

      if (!roInviteProp && !partnerInviteProp) {
        pushHistory(`${redirectRoute}${searchParam}`, true, { isFromPreview });
      }
    }
  }, [isVerified, redirectRoute, selectedPartner]);

  useEffect(() => {
    dispatch(resetLogin());
  }, []);

  useEffect(() => {
    if (isVerified) {
      if (roInviteProp) {
        dispatch(updateProspectId(roInviteProp.prospectId));
        roInviteProp.getProspectInfo();
      }
      if (partnerInviteProp) {
        dispatch(updateProspectId(partnerInviteProp.partnerProspectId));
        partnerInviteProp.getProspectInfo();
      }
    }
  }, [isVerified]);

  useEffect(() => {
    if (verificationError) {
      resetOtpForm();
    }
  }, [verificationError, resetOtpForm]);

  useEffect(() => () => dispatch(verifyClearError()), [dispatch]);

  const handleSendNewCodeLinkClick = useCallback(() => {
    resetOtpForm();
    if (!isGenerating) {
      /* istanbul ignore else */
      if (loginAttempt < MAX_ATTEMPT_ALLOWED) {
        const otpData = { ...applicantInfo };
        otpData.action = "resend";
        otpData.mode = otpType;
        dispatch(generateOtpCode(otpData));
      }
      setLoginAttempt(loginAttempt + 1);
    }
  }, [isGenerating, loginAttempt, dispatch, applicantInfo, resetOtpForm]);

  const submitForm = useCallback(() => {
    let verifyOtpData = {
      code: code.join(""),
      mode: otpType,
      ...(partnerInviteProp && {
        prospectId: partnerInviteProp.partnerProspectId,
        mobileNo: partnerInviteProp.mobileNo,
        email: partnerInviteProp.email,
        sid: partnerInviteProp.sid,
        countryCode: partnerInviteProp.countryCode
      })
    };

    dispatch(verifyOtp({ ...verifyOtpData }));
  }, [dispatch, code, partnerInviteProp]);

  return (
    <Form
      info={info}
      title={title}
      otpType={otpType}
      changeText={changeText}
      applicantInfo={applicantInfo}
      attempts={attempts}
      loginAttempt={loginAttempt}
      isPending={isPending}
      isGenerating={isGenerating}
      verificationError={verificationError}
      otpRef={otpRef}
      code={code}
      setCode={setCode}
      handleSendNewCodeLinkClick={handleSendNewCodeLinkClick}
      submitForm={submitForm}
      maxServerAttemptsExceeded={maxAttemptsExceeded}
      maxAttemptsServerError={maxAttemptsServerError}
      heading={heading}
    />
  );
};
